// TransactionForm.jsx - Revised with multiple max buttons
import React from 'react';
import { ethers } from 'ethers';

const TransactionForm = ({ 
  inputAmount, 
  setInputAmount, 
  isLoading,
  tokenType,
  setTokenType,
  mode,
  walletEthBalance = '0',
  fundEthBalance = '0',
  walletTokenBalance = '0',
  fundTokenBalance = '0'
}) => {
  // Handle input change with validation
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input for user convenience while typing
    if (value === '') {
      setInputAmount('');
      return;
    }
    
    // Only allow numbers and a single decimal point
    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
      // Prevent leading zeros (like 01.5)
      if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
        setInputAmount(value.substring(1));
        return;
      }
      
      // Prevent multiple decimal points
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount <= 1) {
        // Limit to 18 decimal places (ETH precision)
        const parts = value.split('.');
        if (parts.length > 1 && parts[1].length > 18) {
          return;
        }
        
        // Set the value
        setInputAmount(value);
      }
    }
  };

  // Max button handler with specific operation type
  const handleMaxButtonClick = (operation) => {
    try {
      let balance = '0';
      let safePercentage = 995; // Default to 99.5%
      
      // Select the appropriate balance based on operation
      switch (operation) {
        case 'deposit':
          // For deposit, use wallet ETH
          balance = walletEthBalance;
          safePercentage = 990; // Use 99% for ETH to leave room for gas
          break;
        case 'withdraw':
          // For withdraw, use fund ETH
          balance = fundEthBalance;
          break;
        case 'depositTokens':
          // For depositing tokens to fund, use wallet token balance
          balance = walletTokenBalance;
          break;
        case 'withdrawTokens':
          // For withdrawing tokens to wallet, use fund token balance
          balance = fundTokenBalance;
          break;
        case 'mint':
          // For minting, use fund ETH
          balance = fundEthBalance;
          break;
        case 'redeemFund':
          // For redeeming from fund, use fund token balance
          balance = fundTokenBalance;
          break;
        case 'redeemWallet':
          // For redeeming from wallet, use wallet token balance
          balance = walletTokenBalance;
          break;
        default:
          console.warn("Unknown operation type:", operation);
          balance = '0';
      }
      
      console.log(`Max button clicked for ${operation}. Using balance:`, balance);
      
      // If balance is 0 or invalid, just set 0
      if (!balance || balance === '0' || parseFloat(balance) <= 0) {
        console.log("Balance is zero or invalid, setting input to 0");
        setInputAmount('0');
        return;
      }
      
      // Convert the string to a BigNumber
      let balanceBN;
      try {
        balanceBN = ethers.utils.parseEther(balance);
      } catch (error) {
        console.error("Error parsing balance:", error);
        setInputAmount('0');
        return;
      }
      
      // Calculate safe max (99% or 99.5%)
      const safeMax = balanceBN.mul(safePercentage).div(1000);
      
      // Convert back to string
      const formattedMax = ethers.utils.formatEther(safeMax);
      console.log(`Safe max calculated: ${formattedMax} (${safePercentage/10}% of ${balance})`);
      
      // Set the input value
      setInputAmount(formattedMax);
    } catch (error) {
      console.error('Error calculating maximum amount:', error);
      // In case of any error, set a default safe value
      setInputAmount('0');
    }
  };

  // Get appropriate balance based on current mode and token type
  const getAvailableBalance = () => {
    if (mode === 'deposit') {
      return tokenType === 'ETH' ? 
        parseFloat(walletEthBalance).toFixed(6) : 
        parseFloat(walletTokenBalance).toFixed(6);
    } else if (mode === 'mint') {
      // For mint/redeem, show fund token balance
      return parseFloat(fundTokenBalance).toFixed(6);
    }
    return '0.000000';
  };

  // Determine display unit
  const getDisplayUnit = () => {
    if (mode === 'deposit') {
      return tokenType;
    } else if (mode === 'mint') {
      return 'MCIF';
    }
    return '';
  };

  // Check if amount exceeds relevant balance
  const exceedsBalance = () => {
    if (!inputAmount || inputAmount === '0' || inputAmount === '') {
      return false;
    }
    
    let relevantBalance;
    
    // Determine the relevant balance based on mode and token type
    if (mode === 'deposit') {
      relevantBalance = tokenType === 'ETH' ? walletEthBalance : walletTokenBalance;
    } else if (mode === 'mint') {
      relevantBalance = fundTokenBalance;
    } else {
      relevantBalance = '0';
    }
    
    try {
      const amountBN = ethers.utils.parseEther(inputAmount);
      const balanceBN = ethers.utils.parseEther(relevantBalance || '0');
      
      return amountBN.gt(balanceBN);
    } catch (error) {
      console.error('Error comparing amounts:', error);
      return false;
    }
  };

  const isOverBalance = exceedsBalance();

  // Handlers for token selection
  const handleEthClick = () => {
    console.log("ETH clicked");
    setTokenType('ETH');
  };

  const handleMcifClick = () => {
    console.log("MCIF clicked");
    setTokenType('MCIF');
  };

  // Determine which max button to show based on mode and token type
  const renderMaxButtons = () => {
    if (mode === 'deposit') {
      if (tokenType === 'ETH') {
        return (
          <button 
            type="button"
            className="text-blue-500 hover:text-blue-700 font-medium bg-transparent border-none p-0 cursor-pointer"
            onClick={() => handleMaxButtonClick('deposit')}
          >
            Max
          </button>
        );
      } else {
        // For MCIF in deposit mode, show two max buttons
        return (
          <div className="flex space-x-2">
            <button 
              type="button"
              className="text-blue-500 hover:text-blue-700 font-medium bg-transparent border-none p-0 cursor-pointer"
              onClick={() => handleMaxButtonClick('depositTokens')}
              title="Use maximum tokens from wallet"
            >
              Max Deposit
            </button>
            <span className="text-gray-400">|</span>
            <button 
              type="button"
              className="text-blue-500 hover:text-blue-700 font-medium bg-transparent border-none p-0 cursor-pointer"
              onClick={() => handleMaxButtonClick('withdrawTokens')}
              title="Use maximum tokens from fund"
            >
              Max Withdraw
            </button>
          </div>
        );
      }
    } else if (mode === 'mint') {
      // For mint/redeem mode, show two max buttons
      return (
        <div className="flex space-x-2">
          <button 
            type="button"
            className="text-blue-500 hover:text-blue-700 font-medium bg-transparent border-none p-0 cursor-pointer"
            onClick={() => handleMaxButtonClick('mint')}
            title="Use maximum ETH for minting"
          >
            Max Mint
          </button>
          <span className="text-gray-400">|</span>
          <button 
            type="button"
            className="text-blue-500 hover:text-blue-700 font-medium bg-transparent border-none p-0 cursor-pointer"
            onClick={() => handleMaxButtonClick('redeemFund')}
            title="Use maximum tokens for redemption"
          >
            Max Redeem
          </button>
        </div>
      );
    }
    
    return (
      <button 
        type="button"
        className="text-blue-500 hover:text-blue-700 font-medium bg-transparent border-none p-0 cursor-pointer"
        onClick={() => handleMaxButtonClick(mode === 'deposit' ? 'deposit' : 'mint')}
      >
        Max
      </button>
    );
  };

  // Get appropriate balance display text based on current mode and operation
  const getBalanceDisplayText = () => {
    if (mode === 'deposit') {
      if (tokenType === 'ETH') {
        return `Available in Wallet: ${parseFloat(walletEthBalance).toFixed(6)} ETH`;
      } else {
        return (
          <div className="flex flex-col">
            <span>Available in Wallet: {parseFloat(walletTokenBalance).toFixed(6)} MCIF</span>
            <span>Available in Fund: {parseFloat(fundTokenBalance).toFixed(6)} MCIF</span>
          </div>
        );
      }
    } else if (mode === 'mint') {
      return (
        <div className="flex flex-col">
          <span>ETH for Minting: {parseFloat(fundEthBalance).toFixed(6)} ETH</span>
          <span>MCIF for Redemption: {parseFloat(fundTokenBalance).toFixed(6)} MCIF</span>
        </div>
      );
    }
    
    return `Available: ${getAvailableBalance()} ${getDisplayUnit()}`;
  };

  return (
    <div className="bg-white rounded-lg mb-6">
      <div className="flex flex-col">
        {/* Only show token type selector in deposit/withdraw mode */}
        {mode === 'deposit' && (
          <div className="mb-4 pl-4 pt-4 flex gap-2">
            {/* Buttons now on the left side above the input field */}
            <button 
              type="button"
              onClick={handleEthClick}
              className={`px-3 py-1 rounded-md font-medium text-center ${
                tokenType === 'ETH'
                  ? 'bg-blue-500 text-white'
                  : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
              }`}
            >
              ETH
            </button>
            <button 
              type="button"
              onClick={handleMcifClick}
              className={`px-3 py-1 rounded-md font-medium text-center ${
                tokenType === 'MCIF'
                  ? 'bg-blue-500 text-white'
                  : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
              }`}
            >
              MCIF
            </button>
          </div>
        )}

        <div className="flex items-center px-4">
          <div className="mr-4">
            <span className="text-gray-500 font-medium">
              {getDisplayUnit()}
            </span>
          </div>
          <div className="flex-grow">
            <input 
              type="text" 
              value={inputAmount} 
              onChange={handleInputChange}
              disabled={isLoading}
              className={`w-full px-4 py-2 border ${
                isOverBalance
                  ? 'border-red-300 focus:ring-red-500' 
                  : parseFloat(inputAmount) <= 0 && inputAmount !== '' 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
              } rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition`}
              placeholder={
                mode === 'deposit' 
                  ? `Enter ${tokenType} amount greater than 0`
                  : 'Enter MCIF amount to mint/redeem'
              }
            />
          </div>
        </div>

        {/* Enhanced available balance and max buttons */}
        <div className="mt-1 flex justify-between text-xs text-gray-500 px-4">
          <div className="text-left">
            {getBalanceDisplayText()}
          </div>
          
          <div className="flex items-start">
            {renderMaxButtons()}
          </div>
        </div>
        
        {isOverBalance && (
          <p className="mt-1 text-xs text-red-500 px-4">
            Amount exceeds your available balance
          </p>
        )}
        
        {parseFloat(inputAmount) <= 0 && inputAmount !== '' && (
          <p className="mt-1 text-sm text-red-500 px-4">
            Please enter an amount greater than zero
          </p>
        )}
        
        <p className="mt-2 text-sm text-gray-500 px-4 pb-4">
          {mode === 'deposit' 
            ? (tokenType === 'ETH' 
                ? 'Enter the amount of ETH for your transaction' 
                : 'Enter the amount of fund tokens (MCIF) to deposit or withdraw')
            : 'Enter the amount of fund tokens (MCIF) to mint or redeem'
          }
        </p>
      </div>
    </div>
  );
};

export default TransactionForm;